import { useCallback } from 'react';

import { SelectionBounds } from '../utils/selectionCalculations';

/**
 * Hook for manual auto-scrolling to keep selection visible during keyboard navigation
 * Works alongside Pragmatic Drag and Drop's built-in auto-scroll (which only handles drag operations)
 */
export function useKeyboardAutoScroll(
  scrollContainerRef?: React.RefObject<HTMLDivElement>,
  isScrollable: boolean = true
) {
  const ensureSelectionVisible = useCallback(
    (
      selection: SelectionBounds,
      videoDuration: number,
      stripWidth: number,
      selectionWidth: number,
      containerWidth: number,
      overrideStartTime?: number
    ) => {
      if (!scrollContainerRef?.current || !isScrollable) return;

      const scrollContainer = scrollContainerRef.current;
      // Use override time if provided (for keyboard navigation), otherwise use current selection
      const startTime =
        overrideStartTime !== undefined
          ? overrideStartTime
          : selection.startTime;
      const selectionLeft =
        videoDuration > 0 ? (startTime / videoDuration) * stripWidth : 0;
      const selectionRight = selectionLeft + selectionWidth;
      const currentScrollLeft = scrollContainer.scrollLeft;
      const visibleLeft = currentScrollLeft;
      const visibleRight = currentScrollLeft + containerWidth;

      // Check if selection is completely outside viewport (for large jumps like Home/End)
      const isCompletelyOutside =
        selectionRight < visibleLeft || selectionLeft > visibleRight;

      if (isCompletelyOutside) {
        // For large jumps, center the selection in the viewport
        const selectionCenter = selectionLeft + selectionWidth / 2;
        const viewportCenter = containerWidth / 2;
        const newScrollLeft = Math.max(0, selectionCenter - viewportCenter);
        scrollContainer.scrollLeft = newScrollLeft;
      }
    },
    [scrollContainerRef, isScrollable]
  );

  return { ensureSelectionVisible };
}
